import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { ArrowLeft, Calendar, Building, Scale, FileText, Hash, Loader2, BookOpen, Tags } from 'lucide-react';
import { jurisprudentieService } from '@shared/services/jurisprudentie.service';
import type { Database } from '@shared/types/database';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { JurisprudenceRenderer } from '@shared/components/JurisprudenceRenderer';

type JurisprudentieWithRelations = Database['public']['Tables']['jurisprudentie']['Row'] & {
  instanties?: Database['public']['Tables']['instanties']['Row'];
  jurisprudentie_rechtsgebieden?: Array<{
    rechtsgebieden: Database['public']['Tables']['rechtsgebieden']['Row'];
  }>;
  jurisprudentie_zaak_types?: Array<{
    zaak_types: Database['public']['Tables']['zaak_types']['Row'];
  }>;
  jurisprudentie_wetsartikelen?: Array<{
    wetsartikelen: Database['public']['Tables']['wetsartikelen']['Row'];
  }>;
  jurisprudentie_themas?: Array<{
    themas: Database['public']['Tables']['themas']['Row'];
  }>;
};

export default function CaseDetailPage() {
  const { id } = useParams();
  const [jurisprudentie, setJurisprudentie] = useState<JurisprudentieWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCase = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const data = await jurisprudentieService.getById(id);
        if (data) {
          setJurisprudentie(data as JurisprudentieWithRelations);
        } else {
          setError('Uitspraak niet gevonden');
        }
      } catch (err) {
        setError('Er is een fout opgetreden bij het ophalen van de uitspraak');
        console.error('Error fetching case:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCase();
  }, [id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Decorative blur circles */}
        <div className="absolute top-20 right-0 -mr-40 w-80 h-80 bg-primary/10 dark:bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-40 left-0 -ml-40 w-96 h-96 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !jurisprudentie) {
    return (
      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Decorative blur circles */}
        <div className="absolute top-20 right-0 -mr-40 w-80 h-80 bg-primary/10 dark:bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-40 left-0 -ml-40 w-96 h-96 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative container mx-auto px-4 py-8">
          <div className="mb-6">
            <Link to="/search">
              <Button variant="ghost" className="flex items-center gap-2 rounded-xl hover:bg-primary/10 transition-colors">
                <ArrowLeft className="h-4 w-4" />
                Terug naar zoekresultaten
              </Button>
            </Link>
          </div>
          <Card className="rounded-2xl border-border/50 shadow-sm">
            <CardContent className="py-8">
              <p className="text-center text-muted-foreground">
                {error || 'Uitspraak niet gevonden'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Decorative blur circles */}
      <div className="absolute top-20 right-0 -mr-40 w-80 h-80 bg-primary/10 dark:bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-0 -ml-40 w-96 h-96 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative container mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/search">
            <Button variant="ghost" className="flex items-center gap-2 rounded-xl hover:bg-primary/10 transition-colors">
              <ArrowLeft className="h-4 w-4" />
              Terug naar zoekresultaten
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Metadata sidebar */}
        <div className="lg:col-span-1 space-y-4">
          <div className="lg:sticky lg:top-20">
            <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-lg transition-all duration-300 dark:border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold">Uitspraak informatie</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
              {/* ECLI */}
              <div className="group">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <div className="p-1.5 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <Hash className="h-3.5 w-3.5 text-primary" />
                  </div>
                  ECLI
                </div>
                <p className="text-sm font-mono bg-muted/50 px-2 py-1 rounded-lg">{jurisprudentie.ecli}</p>
              </div>

              {/* Instantie */}
              {jurisprudentie.instanties && (
                <div className="group">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <div className="p-1.5 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                      <Building className="h-3.5 w-3.5 text-primary" />
                    </div>
                    Instantie
                  </div>
                  <p className="text-sm font-medium">{jurisprudentie.instanties.naam}</p>
                  {jurisprudentie.instanties.locatie && (
                    <p className="text-xs text-muted-foreground">{jurisprudentie.instanties.locatie}</p>
                  )}
                </div>
              )}

              {/* Uitspraak datum */}
              <div className="group">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <div className="p-1.5 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <Calendar className="h-3.5 w-3.5 text-primary" />
                  </div>
                  Uitspraakdatum
                </div>
                <p className="text-sm">{formatDate(jurisprudentie.uitspraak_datum)}</p>
              </div>

              {/* Publicatie datum */}
              <div className="group">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <div className="p-1.5 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <FileText className="h-3.5 w-3.5 text-primary" />
                  </div>
                  Publicatiedatum
                </div>
                <p className="text-sm">{formatDate(jurisprudentie.publicatie_datum)}</p>
              </div>

              {/* Zaaknummer */}
              {jurisprudentie.zaaknummer && (
                <div className="group">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <div className="p-1.5 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                      <Hash className="h-3.5 w-3.5 text-primary" />
                    </div>
                    Zaaknummer
                  </div>
                  <p className="text-sm">{jurisprudentie.zaaknummer}</p>
                </div>
              )}

              {/* Rechtsgebieden */}
              {jurisprudentie.jurisprudentie_rechtsgebieden && jurisprudentie.jurisprudentie_rechtsgebieden.length > 0 && (
                <div className="group">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <div className="p-1.5 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                      <Scale className="h-3.5 w-3.5 text-primary" />
                    </div>
                    Rechtsgebieden
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {jurisprudentie.jurisprudentie_rechtsgebieden.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-1 rounded-lg bg-primary/10 text-primary border border-primary/20 text-xs font-medium"
                      >
                        {item.rechtsgebieden.naam}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Zaak types */}
              {jurisprudentie.jurisprudentie_zaak_types && jurisprudentie.jurisprudentie_zaak_types.length > 0 && (
                <div className="group">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <div className="p-1.5 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                      <FileText className="h-3.5 w-3.5 text-primary" />
                    </div>
                    Type uitspraak
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {jurisprudentie.jurisprudentie_zaak_types.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-1 rounded-lg bg-secondary text-secondary-foreground text-xs font-medium"
                      >
                        {item.zaak_types.naam}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Wetsartikelen */}
              {jurisprudentie.jurisprudentie_wetsartikelen && jurisprudentie.jurisprudentie_wetsartikelen.length > 0 && (
                <div className="group">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <div className="p-1.5 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                      <BookOpen className="h-3.5 w-3.5 text-primary" />
                    </div>
                    Wetsartikelen
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {jurisprudentie.jurisprudentie_wetsartikelen.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 text-xs font-medium"
                      >
                        {item.wetsartikelen.volledige_referentie}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Themas */}
              {jurisprudentie.jurisprudentie_themas && jurisprudentie.jurisprudentie_themas.length > 0 && (
                <div className="group">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                    <div className="p-1.5 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                      <Tags className="h-3.5 w-3.5 text-primary" />
                    </div>
                    Thema's
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {jurisprudentie.jurisprudentie_themas.map((item, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-1 rounded-lg bg-primary/10 text-primary border border-primary/20 text-xs font-medium"
                      >
                        {item.themas.naam}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
            </Card>
          </div>
        </div>

        {/* Main content */}
        <div className="lg:col-span-3">
          <Card className="rounded-2xl border-border/50 shadow-sm hover:shadow-lg transition-all duration-300 dark:border-border">
            <CardHeader>
              <CardTitle className="text-xl font-semibold">{jurisprudentie.titel}</CardTitle>
              {jurisprudentie.samenvatting && (
                <div className="mt-4 p-4 bg-muted/50 rounded-xl border border-border/50 overflow-hidden">
                  <h3 className="font-semibold text-sm mb-2">Samenvatting</h3>
                  <div className="text-sm text-muted-foreground leading-relaxed">
                    {(() => {
                      const formatKey = (key: string) => {
                        const words = key.split('_');
                        return words
                          .map((word, index) => index === 0
                            ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                            : word.toLowerCase()
                          )
                          .join(' ');
                      };

                      // Normalize text to prevent markdown parsing issues with indentation/spacing
                      const normalizeMarkdownText = (text: string) => {
                        return text
                          // Remove excessive leading/trailing spaces from lines
                          .split('\n')
                          .map(line => line.trim())
                          .join('\n')
                          // Remove markdown blockquote markers that might be causing issues
                          .replace(/^>\s*/gm, '')
                          // Collapse multiple newlines into double newlines
                          .replace(/\n{3,}/g, '\n\n');
                      };

                      try {
                        const parsed = JSON.parse(jurisprudentie.samenvatting);
                        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
                          return Object.entries(parsed)
                            .filter(([_, value]) => value && String(value).trim() !== '')
                            .map(([key, value]) => (
                              <div key={key} className="mb-3 last:mb-0">
                                <h3 className="font-semibold text-foreground mb-1">{formatKey(key)}</h3>
                                <div className="prose prose-sm dark:prose-invert max-w-none break-words overflow-hidden [&_*]:break-words [&_*]:overflow-wrap-anywhere [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_li]:my-0 [&_p]:my-2 [&_strong]:font-semibold [&_blockquote]:break-words [&_blockquote]:overflow-wrap-anywhere [&_blockquote]:whitespace-normal [&_blockquote]:max-w-full">
                                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {normalizeMarkdownText(String(value))}
                                  </ReactMarkdown>
                                </div>
                              </div>
                            ));
                        }
                        return (
                          <div className="prose prose-sm dark:prose-invert max-w-none break-words overflow-hidden [&_*]:break-words [&_*]:overflow-wrap-anywhere [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_li]:my-0 [&_p]:my-2 [&_strong]:font-semibold [&_blockquote]:break-words [&_blockquote]:overflow-wrap-anywhere [&_blockquote]:whitespace-normal [&_blockquote]:max-w-full">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {normalizeMarkdownText(jurisprudentie.samenvatting)}
                            </ReactMarkdown>
                          </div>
                        );
                      } catch {
                        return (
                          <div className="prose prose-sm dark:prose-invert max-w-none break-words overflow-hidden [&_*]:break-words [&_*]:overflow-wrap-anywhere [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_li]:my-0 [&_p]:my-2 [&_strong]:font-semibold [&_blockquote]:break-words [&_blockquote]:overflow-wrap-anywhere [&_blockquote]:whitespace-normal [&_blockquote]:max-w-full">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {normalizeMarkdownText(jurisprudentie.samenvatting)}
                            </ReactMarkdown>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent>
              <h3 className="font-semibold mb-4">Volledige tekst</h3>
              <JurisprudenceRenderer content={jurisprudentie.volledige_html || ''} />
            </CardContent>
          </Card>
        </div>
      </div>
      </div>
    </div>
  );
}